import _ from 'lodash'
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { subMonths } from 'date-fns'

import {
  deleteAnnouncementService,
  getActivityStreamService,
  getAnnouncementsService,
  postActivityStreamMessageService,
} from 'common/services/notifications'
import { changeModal } from 'services/actions'
import { translateResponseError } from 'common/utils'
import { validateField, validateFields } from 'common/validator'

import {
  addFieldError,
  addFieldsErrors,
  clearFieldError,
  DELETE_ANNOUNCEMENT,
  resetAnnouncementForm,
  SUBMIT_ANNOUNCEMENT,
  VALIDATE_ANNOUNCEMENT_FIELD,
} from './actions'
import type { SubmitAnnouncementPayload } from './actions'

interface PrepareDataPayload extends SubmitAnnouncementPayload {
  id?: { value: string }
}

function prepareData(payload: PrepareDataPayload): {
  scope: string
  message: { title: string; content: string; sticky: boolean; thumbnail_url?: string }
  activity_stream_id?: string
} {
  const message: { title: string; content: string; sticky: boolean; thumbnail_url?: string } = {
    title: payload.title.value,
    content: payload.content.value,
    sticky: true,
  }

  if (payload.thumbnail_url && payload.thumbnail_url.value) {
    message.thumbnail_url = payload.thumbnail_url.value
  }

  if ('id' in payload && payload.id) {
    return {
      activity_stream_id: payload.id.value,
      scope: 'auth',
      message,
    }
  }

  return {
    scope: 'auth',
    message,
  }
}

export function* submitAnnouncementSaga({
  payload,
}: {
  payload: SubmitAnnouncementPayload
}): Generator<unknown, void, unknown> {
  const errors = validateFields(
    payload as unknown as Parameters<typeof validateFields>[0],
  ) as Record<string, string | undefined>
  if (_.isEmpty(errors)) {
    const data = prepareData(payload as PrepareDataPayload)
    // requestSaga signature from buildService doesn't match redux-saga call overload
    const announcementResult = (yield call(
      postActivityStreamMessageService.requestSaga as (arg: {
        payload: { data: unknown }
      }) => Generator,
      { payload: { data } },
    )) as { error?: { message?: string } }

    if (announcementResult.error) {
      yield put(
        addFieldError({
          field: 'base',
          error: translateResponseError(
            announcementResult.error as Parameters<typeof translateResponseError>[0],
          ),
        }),
      )
    } else {
      yield put(changeModal({ isOpen: false }))
      yield put(resetAnnouncementForm())
      yield put(
        getAnnouncementsService.actions.request({
          data: {
            from: subMonths(new Date(), 6).toISOString(),
            max_rows: 30,
            scope: 'auth',
          },
        }),
      )
      yield put(getActivityStreamService.actions.request())
    }
  } else {
    yield put(addFieldsErrors(errors))
  }
}

export function* deleteAnnouncementSaga({
  payload: { announcementId },
}: {
  payload: { announcementId: string }
}): Generator<unknown, void, unknown> {
  const result = (yield call(
    deleteAnnouncementService.requestSaga as (arg: {
      payload: { data: { activity_stream_id: string } }
    }) => Generator,
    {
      payload: {
        data: {
          activity_stream_id: announcementId,
        },
      },
    },
  )) as { error?: unknown }

  if (!result.error) {
    yield put(changeModal({ isOpen: false }))
    yield put(
      getAnnouncementsService.actions.request({
        data: {
          from: subMonths(new Date(), 6).toISOString(),
          max_rows: 30,
          scope: 'auth',
        },
      }),
    )
    yield put(getActivityStreamService.actions.request())
  }
}

export function* validateFieldSaga({
  payload: { field, value, validations },
}: {
  payload: { field: string; value: string; validations?: string }
}): Generator<unknown, void, unknown> {
  const error = validateField({ field, value, fieldValidations: validations ?? '' })
  const errorStr = typeof error === 'string' ? error : error != null ? String(error) : undefined
  if (errorStr) {
    yield put(addFieldError({ field, error: errorStr }))
  } else {
    yield put(clearFieldError({ field }))
  }
}

export default function* saga(): Generator<unknown, void, unknown> {
  yield all([
    // redux-saga types expect ActionPattern; string is valid at runtime
    // @ts-expect-error action pattern strings are valid runtime values
    takeEvery(VALIDATE_ANNOUNCEMENT_FIELD, validateFieldSaga),
    // @ts-expect-error action pattern strings are valid runtime values
    takeLatest(SUBMIT_ANNOUNCEMENT, submitAnnouncementSaga),
    // @ts-expect-error action pattern strings are valid runtime values
    takeLatest(DELETE_ANNOUNCEMENT, deleteAnnouncementSaga),
  ])
}
